import { io, Socket } from 'socket.io-client';
import { SocketEventType, SocketMessage } from './types';
import { setupHandlers } from './subscriptions/setupHandlers';

interface AuthenticationResponse {
  success: boolean;
  error?: string;
}

/**
 * WebSocket service to manage connection with the chat server
 */
class SocketService {
  private socket: Socket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private initialReconnectDelay = 1000; // 1 second
  private authToken: string | null = null;
  private authenticated = false;

  /**
   * Initialize the socket connection
   * @param stytchUserId Stytch user ID for authentication
   * @returns Promise resolving to a boolean indicating success
   */
  public initialize(stytchUserId: string): Promise<boolean> {
    return new Promise(async (resolve) => {
      if (this.socket && this.socket.connected && this.authenticated) {
        resolve(true);
        return;
      }

      // Store the token for reconnections
      this.authToken = stytchUserId;

      // Create Socket.IO instance with reconnection options
      this.socket = io(window.location.origin, {
        path: '/socket.io',
        reconnection: true,
        reconnectionAttempts: this.maxReconnectAttempts,
        reconnectionDelay: this.initialReconnectDelay,
        reconnectionDelayMax: 30000, // 30 seconds
        timeout: 20000,
        autoConnect: true,
      });

      // Handle system events
      this.setupSystemEventHandlers();

      // Wait for socket to connect
      if (!this.socket.connected) {
        await new Promise<void>((resolve) => {
          this.socket?.once('connect', () => {
            resolve();
          });
        });
      }

      // Authenticate with the server
      try {
        const authSuccess = await this.authenticateSocket(stytchUserId);
        this.authenticated = authSuccess;

        if (authSuccess && this.socket) {
          // Register event handlers for messages
          setupHandlers(this.socket);
        }

        resolve(authSuccess);
      } catch (error) {
        console.error('Failed to authenticate:', error);
        this.authenticated = false;
        resolve(false);
      }
    });
  }

  /**
   * Set up event handlers for system events
   * @private
   */
  private setupSystemEventHandlers() {
    if (!this.socket) return;

    // Handle connection event
    this.socket.on('connect', () => {
      console.info('WebSocket connected');

      // Reset reconnect attempts on successful connection
      this.reconnectAttempts = 0;

      // Re-authenticate if we have a token
      if (this.authToken && !this.authenticated) {
        this.authenticateSocket(this.authToken)
          .then((success) => {
            this.authenticated = success;
          })
          .catch((err) => {
            console.error('Re-authentication failed:', err);
            this.authenticated = false;
          });
      }
    });

    // Handle disconnection
    this.socket.on('disconnect', (reason) => {
      console.info(`WebSocket disconnected: ${reason}`);
      this.authenticated = false;
    });

    // Handle reconnection attempts
    this.socket.io.on('reconnect_attempt', (attempt) => {
      this.reconnectAttempts = attempt;
      console.info(`WebSocket reconnection attempt ${attempt}/${this.maxReconnectAttempts}`);
    });

    // Handle successful reconnection
    this.socket.io.on('reconnect', () => {
      console.info('WebSocket reconnected');

      // Re-authenticate after reconnection
      if (this.authToken) {
        this.authenticateSocket(this.authToken)
          .then((success) => {
            this.authenticated = success;

            if (success && this.socket) {
              // Re-register event handlers for messages
              setupHandlers(this.socket);
            }
          })
          .catch((err) => {
            console.error('Re-authentication failed:', err);
            this.authenticated = false;
          });
      }
    });

    // Handle reconnection failure
    this.socket.io.on('reconnect_failed', () => {
      console.error('WebSocket reconnection failed after max attempts');
    });

    // Handle connection errors
    this.socket.on('connect_error', (error) => {
      console.error('WebSocket connection error', error);
    });
  }

  /**
   * Authenticate the socket with the server
   * @param stytchUserId The Stytch user ID for authentication
   * @returns Promise resolving to a boolean indicating success
   */
  private authenticateSocket(stytchUserId: string): Promise<boolean> {
    if (!this.socket || !this.socket.connected) {
      return Promise.reject(new Error('Socket not connected'));
    }

    return new Promise<boolean>((resolve) => {
      this.socket?.emit(
        SocketEventType.Authentication,
        stytchUserId,
        (response: AuthenticationResponse) => {
          if (response.success) {
            console.info('Successfully authenticated with WebSocket server');
            resolve(true);
          } else {
            console.error('Failed to authenticate with WebSocket server', response.error);
            resolve(false);
          }
        },
      );
    });
  }

  // pub to server, needs server to respond with callback
  public async emitWithAck<Req, Res>(
    event: SocketEventType,
    message: SocketMessage<Req>,
  ): Promise<Res> {
    // If not connected or not authenticated, try to reconnect
    if (!this.isConnected()) {
      if (!this.authToken) {
        console.error('Cannot emit event: No auth token available for reconnect');
        throw new Error('Cannot emit event: No auth token available for reconnect');
      }
      const connected = await this.initialize(this.authToken);
      if (!connected) {
        console.error('Cannot emit event: WebSocket could not reconnect');
        throw new Error('Cannot emit event: WebSocket could not reconnect');
      }
    }

    return this.socket!.emitWithAck(event, message);
  }

  /**
   * Disconnect the socket
   */
  public disconnect(): void {
    if (this.socket) {
      console.info('Disconnecting WebSocket');

      // Reset authentication state
      this.authenticated = false;

      // Disconnect the socket
      this.socket.disconnect();
      this.socket = null;

      console.info('WebSocket disconnected successfully');
    }
  }

  /**
   * Check if socket is connected and authenticated
   * @returns Boolean indicating connection status
   */
  public isConnected(): boolean {
    return !!(this.socket && this.socket.connected && this.authenticated);
  }
}

// Create a singleton instance
export const socketService = new SocketService();
